

import React, { useState, useEffect, useRef } from 'react';
import useGameBase from '../../hooks/useGameBase';

function GuessTheSongGame({ gameState, gamePlayers, user, socket, roomId }) {
  const { submitAction } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  const { previewUrl, choices, answers, answerTimes, phase, currentRound, numRounds, correctAnswer, scores, players, finished, timeLimit, setupError } = gameState;
  const [timeLeft, setTimeLeft] = useState(timeLimit || 30);
  const timerRef = useRef();

  useEffect(() => {
    if (phase === 'question' && !finished) {
      setTimeLeft(timeLimit || 30);
      const start = Date.now();
      timerRef.current = setInterval(() => {
        const elapsed = (Date.now() - start) / 1000;
        setTimeLeft(Math.max(0, Math.ceil((timeLimit || 30) - elapsed)));
        if (elapsed >= (timeLimit || 30)) {
          clearInterval(timerRef.current);
        }
      }, 100);
      return () => clearInterval(timerRef.current);
    } else {
      setTimeLeft(timeLimit || 30);
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [phase, currentRound, timeLimit, finished]);
  const [selected, setSelected] = useState(null);
  const [volume, setVolume] = useState(1);
  const audioRef = useRef();
  const [needUserPlay, setNeedUserPlay] = useState(false);

  useEffect(() => {
    setSelected(null);
  }, [previewUrl, phase, currentRound]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume, previewUrl]);

  // Attempt to auto-play when previewUrl updates. If browser blocks it (mobile), show a tap-to-play fallback.
  useEffect(() => {
    setNeedUserPlay(false);
    if (!previewUrl || !audioRef.current) return;
    const a = audioRef.current;
    a.pause();
    a.currentTime = 0;
    const playPromise = a.play();
    if (playPromise && typeof playPromise.then === 'function') {
      playPromise.then(() => {
        setNeedUserPlay(false);
      }).catch((err) => {
        // Autoplay blocked — require user gesture
        setNeedUserPlay(true);
      });
    }
  }, [previewUrl]);

  const handleClick = idx => {
    if (selected !== null || phase !== 'question' || finished) return;
    setSelected(idx);
    submitAction({ type: 'answer', choiceIdx: idx });
  };

  // (correct index not needed here)

  return (
    <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100 dark:bg-gray-900 p-4">
      {setupError ? (
        <div className="w-full max-w-xl rounded-xl border border-amber-300 bg-amber-50 p-4 text-center text-amber-900 shadow-sm dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100">
          <div className="text-lg font-bold">Guess The Song couldn&apos;t start</div>
          <div className="mt-2 text-sm">{setupError}</div>
        </div>
      ) : null}

      {/* Progress bar for time limit */}
      {phase === 'question' && !finished && !setupError && (
        <div className="w-full max-w-md mb-2">
          <div className="h-3 bg-gray-300 dark:bg-gray-700 rounded overflow-hidden">
            <div
              className="h-3 bg-blue-500 dark:bg-blue-400"
              style={{ width: `${(1 - timeLeft / (timeLimit || 30)) * 100}%`, transition: 'width 0.1s linear' }}
            ></div>
          </div>
          <div className="text-xs text-gray-600 dark:text-gray-300 mt-1 text-right">{timeLeft}s left</div>
        </div>
      )}
    {phase !== 'finished' && !finished && !setupError && (
        <div className="mb-2 text-gray-700 dark:text-gray-200">Round {currentRound + 1} of {numRounds}</div>
      )}
      {setupError ? null : phase === 'finished' ? (
        <div className="text-lg font-bold mb-4 dark:text-gray-100">Game Finished!</div>
      ) : phase === 'reveal' ? (
        <div className="text-lg font-bold mb-4 dark:text-gray-100">The correct answer was:<br />
          <span className="text-green-700 dark:text-green-400">{correctAnswer?.artist} — {correctAnswer?.title}</span>
        </div>
      ) : null}
      {previewUrl && phase !== 'finished' && !finished && !setupError && (
        <>
          <audio
            ref={audioRef}
            autoPlay
            playsInline
            preload="auto"
            crossOrigin="anonymous"
            src={previewUrl}
            className="mb-2"
          />
          {needUserPlay && (
            <div className="mb-2 w-full flex items-center justify-center">
              <button
                onClick={() => {
                  try {
                    audioRef.current && audioRef.current.play();
                    setNeedUserPlay(false);
                  } catch (e) {
                    // ignore
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded"
              >
                Tap to play audio
              </button>
            </div>
          )}
          <div className="flex items-center gap-2 mb-4">
            <label htmlFor="volume-slider" className="text-sm dark:text-gray-200">Volume:</label>
            <input
              id="volume-slider"
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={volume}
              onChange={e => setVolume(Number(e.target.value))}
              className="w-32"
            />
          </div>
        </>
      )}
      {phase === 'question' && !finished && !setupError && (
        <div className="flex flex-col gap-2 w-full max-w-md">
          {choices?.map((c, idx) => (
            <div key={idx} className="flex items-center gap-2 w-full">
              <button
                onClick={() => handleClick(idx)}
                className={`flex-1 min-w-0 p-3 rounded border text-left font-semibold transition-colors dark:text-gray-100 ${selected === idx ? (c.correct ? 'bg-green-500' : 'bg-red-500') : 'bg-gray-200 dark:bg-gray-700'} border-gray-400 dark:border-gray-600`}
                style={{ width: '70%' }}
                disabled={selected !== null}
              >
                {c.artist} — {c.title}
              </button>
            </div>
          ))}
        </div>
      )}
      {phase === 'reveal' && !finished && !setupError && (
          <div className="mt-4 dark:text-gray-100">
            <span className='font-bold text-lg mb-2 dark:text-gray-100'>Answers:</span>
            <ul className="mt-2">
              {Object.entries(answers || {}).map(([player, idx]) => {
                const playerObj = players?.find(p => p.socketId === player || p.userId === player);
                const displayName = playerObj?.username || player;
                const timeMs = answerTimes?.[player];
                const timeTxt = timeMs != null ? `${(timeMs / 1000).toFixed(1)}s` : null;
                return (
                  <li key={player} className="dark:text-gray-100 mb-2">
                    {choices[idx]?.correct
                      ? <span className="text-green-700 dark:text-green-400 font-semibold">{displayName}</span>
                      : <span className="text-red-700 dark:text-red-400 font-semibold">{displayName}</span>}
                    {timeTxt && <span className="text-gray-500 dark:text-gray-400 text-sm ml-1">({timeTxt})</span>}
                    {' '}: {choices[idx]?.artist} — {choices[idx]?.title}{' '}
                    {choices[idx]?.correct
                      ? <span className="text-green-700 dark:text-green-400">(Correct)</span>
                      : <span className="text-red-700 dark:text-red-400">(Wrong)</span>}
                  </li>
                );
              })}
            </ul>
            <div className="mt-4">
              <h4 className="font-bold text-lg mb-2 dark:text-gray-100">Song Links (Open in Deezer in new tab):</h4>
              <ul>
                {choices?.map((c, idx) => (
                  <li key={idx} className="text-gray-800 dark:text-gray-100 mb-1 underline">
                    <a href={c.link} target="_blank" rel="noopener noreferrer">{c.artist} — {c.title}</a>
                  </li>
                ))}
              </ul>
            </div>
          </div>



      )}
      {phase === 'finished' && !setupError && (
        <div className="mt-4 w-full max-w-md">
          <h3 className="font-bold mb-2 dark:text-gray-100">Final Scores:</h3>
          <ul>
            {players?.map(p => (
              <li key={p.socketId || p.userId} className="dark:text-gray-100">
                <span className="font-bold">{user.socketId === p.socketId || user.userId === p.userId ? "You" : p.username || p.userId}</span>: {scores?.[p.socketId] ?? scores?.[p.userId] ?? 0}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


GuessTheSongGame.Guide = function GuessTheSongGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">How to Play Guess the Song</h3>

      <p className="mb-2">
        <strong>Objective:</strong> Guess the correct song title and artist from a short audio clip. The player with the highest score at the end of all rounds wins!
      </p>
        <p>30 seconds of each clip will be played for players to make their guesses.</p>
    </div>
  );
};

export default GuessTheSongGame;